import React, { useState, useEffect } from 'react';
import { useJsApiLoader, Libraries } from '@react-google-maps/api';
import Select from 'react-select';

const libraries: Libraries = ['places'];

interface CustomGoogleAutoCompleteProps {
  preloadedAddresses: string[];
  onAddressSelect: (address: string) => void;
  initialValue?: string; // Add prop to set initial value from parent
}

interface AddressOption {
  label: string;
  value: string | google.maps.places.AutocompletePrediction;
}

const CustomGoogleAutoComplete: React.FC<CustomGoogleAutoCompleteProps> = ({ preloadedAddresses, onAddressSelect, initialValue }) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [options, setOptions] = useState<AddressOption[]>([]);
  const [value, setValue] = useState<AddressOption | null>(() => {
    // Initialize from localStorage if available
    const storedAddress = localStorage.getItem('createJob_selectedAddress');
    if (storedAddress) {
      return { label: storedAddress, value: storedAddress };
    }
    // Fallback to initialValue prop if provided
    if (initialValue) {
      return { label: initialValue, value: initialValue };
    }
    return null;
  });
  const [googleOptions, setGoogleOptions] = useState<AddressOption[]>([]);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });

  useEffect(() => {
    const formattedOptions: AddressOption[] = preloadedAddresses.map((address) => ({
      label: address,
      value: address,
    }));
    setOptions(formattedOptions);
  }, [preloadedAddresses]);

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    fetchGoogleSuggestions(newValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const fetchGoogleSuggestions = async (input: string) => {
    if (input.length > 0) {
      const service = new google.maps.places.AutocompleteService();
      const request: google.maps.places.AutocompletionRequest = {
        input,
        componentRestrictions: { country: ['us', 'ca'] },
        types: ['address'],
      };

      service.getPlacePredictions(request, (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          const suggestions: AddressOption[] = predictions.map((prediction) => ({
            label: prediction.description,
            value: prediction,
          }));
          setGoogleOptions(suggestions);
        } else {
          setGoogleOptions([]);
        }
      });
    } else {
      setGoogleOptions([]);
    }
  };

  const handleSelect = async (selectedOption: AddressOption | null) => {
    if (selectedOption === null) {
      setValue(null);
      localStorage.removeItem('createJob_selectedAddress');
      onAddressSelect('');
      return;
    }

    let finalAddressString = '';

    if (typeof selectedOption.value !== 'string' && selectedOption.value.place_id) {
      const result = await geocodeByPlaceId(selectedOption.value.place_id);
      if (result && result.length > 0) {
        const addressObject = parseAddressComponents(result[0].address_components);
        finalAddressString = assembleAddressString(addressObject);

        setValue({ label: finalAddressString, value: finalAddressString });
      }
    } else {
      finalAddressString = selectedOption.value as string;
      setValue(selectedOption);
    }

    // Save to localStorage
    localStorage.setItem('createJob_selectedAddress', finalAddressString);
    onAddressSelect(finalAddressString);
  };

  const geocodeByPlaceId = (placeId: string): Promise<google.maps.GeocoderResult[]> =>
    new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ placeId }, (results, status) => {
        if (status === 'OK' && results) {
          resolve(results);
        } else {
          reject(new Error('Failed to geocode place ID.'));
        }
      });
    });

  interface AddressComponent {
    street_number?: string;
    route?: string;
    locality?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  }

  const parseAddressComponents = (addressComponents: google.maps.GeocoderAddressComponent[]): AddressComponent => {
    const address: AddressComponent = {};
    addressComponents.forEach((component) => {
      if (component.types.includes('street_number')) {
        address.street_number = component.long_name;
      } else if (component.types.includes('route')) {
        address.route = component.long_name;
      } else if (component.types.includes('locality')) {
        address.locality = component.long_name;
      } else if (component.types.includes('administrative_area_level_1')) {
        address.state = component.short_name;
      } else if (component.types.includes('postal_code')) {
        address.postal_code = component.long_name;
      } else if (component.types.includes('country')) {
        address.country = component.long_name;
      }
    });
    return address;
  };

  const assembleAddressString = (addressObject: AddressComponent): string => {
    return `${addressObject.street_number || ''} ${addressObject.route || ''}, ${addressObject.locality || ''}, ${addressObject.state || ''} ${addressObject.postal_code || ''}, ${addressObject.country || ''}`
      .trim()
      .replace(/ ,/g, ',')
      .replace(/ ,/g, ',');
  };

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderRadius: '4px',
      minHeight: '42px',
      fontSize: '1.035rem',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : provided.boxShadow,
      borderColor: state.isFocused ? '#80bdff' : '#ced4da',
      '&:hover': {
        borderColor: state.isFocused ? '#80bdff' : '#adb5bd'
      }
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0',
      paddingTop: '0',
      paddingBottom: '0',
      fontSize: '1.035rem',
      lineHeight: '1.725',
      color: '#495057',
      width: 'auto',
      minWidth: '2px', // Prevent input from shrinking too much
      flex: '1 1 auto' // Allow input to grow and shrink as needed
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: '0.69rem 0.92rem',
      fontSize: '1.035rem',
      lineHeight: '1.725',
      minHeight: '42px',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'nowrap',
      overflow: 'hidden' // Keep overflow hidden for the container
    }),
    placeholder: (provided: any) => ({
      ...provided,
      fontSize: '1.035rem',
      color: '#6c757d',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }),
    singleValue: (provided: any) => ({
      ...provided,
      fontSize: '1.035rem',
      color: '#495057',
      maxWidth: 'calc(100% - 20px)',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      fontSize: '1.035rem'
    }),
    menuList: (provided: any) => ({
      ...provided,
      fontSize: '1.035rem'
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      fontSize: '1.035rem',
      padding: '0.69rem 0.92rem',
      backgroundColor: state.isSelected 
        ? '#007bff' 
        : state.isFocused 
        ? '#f8f9fa' 
        : 'white',
      color: state.isSelected ? 'white' : '#495057',
      '&:hover': {
        backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
      }
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      padding: '0 0.5rem'
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      padding: '0.5rem'
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      padding: '0.5rem'
    })
  };

  const mergedOptions = [...options, ...googleOptions];

  return (
    <div style={{ width: '100%' }}>
      <Select<AddressOption>
        options={mergedOptions}
        value={value}
        onChange={handleSelect}
        onInputChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={isFocused ? "" : "Select or enter an address"}
        inputValue={inputValue}
        styles={customStyles}
        isClearable
        isSearchable
        menuPlacement="auto"
        menuPortalTarget={document.body}
        menuShouldScrollIntoView={false}
      />
    </div>
  );
};

export default CustomGoogleAutoComplete;